import {
  FC, useCallback, useEffect, useState,
} from 'react';
import { UnFormalizedZipListItem } from 'entities/unFormalizedZip';
import { UnFormalizedZipRequestDto, UnFormalizedZipResponseDto } from 'shared/api';
import { useUnFormalizedZip } from '../hooks/useUnFormalizedZip';
import { UnFormalizedZipTable, UnFormalizedZipForm, FormalizeModal } from '../components';

export const UnFormalizedZipPage: FC = () => {
  const {
    unFormalizedZips,
    loadUnFormalizedZips,
    createNew,
    updateExisting,
    deleteById,
    loadDetail,
  } = useUnFormalizedZip();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<UnFormalizedZipRequestDto | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormalizeModalOpen, setIsFormalizeModalOpen] = useState(false);
  const [formalizingItem, setFormalizingItem] = useState<UnFormalizedZipListItem | null>(null);

  useEffect(() => {
    loadUnFormalizedZips(UnFormalizedZipResponseDto.status.NEW);
  }, []);

  const mapDetailToForm = useCallback((detail?: UnFormalizedZipResponseDto | null): UnFormalizedZipRequestDto | null => {
    if (!detail) return null;
    return {
      engineId: detail.engineId || 0,
      description: detail.description || '',
      docUrlUuids: detail.docUrlUuids,
      status: detail.status as unknown as UnFormalizedZipRequestDto.status,
    };
  }, []);

  const handleEdit = useCallback(async (item: UnFormalizedZipListItem) => {
    setEditingId(item.id || null);
    setIsModalOpen(true);
    if (item.id) {
      const res = await loadDetail(item.id);
      if (res.success) {
        setEditingItem(mapDetailToForm(res.detail));
      }
    }
  }, [loadDetail, mapDetailToForm]);

  const handleDelete = useCallback(async (id: number) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Удалить запись?')) {
      await deleteById(id);
      await loadUnFormalizedZips();
    }
  }, [deleteById, loadUnFormalizedZips]);

  const handleSubmit = useCallback(async (data: UnFormalizedZipRequestDto) => {
    if (editingId) {
      await updateExisting(editingId, data);
    } else {
      await createNew(data);
    }
    setIsModalOpen(false);
    setEditingItem(null);
    setEditingId(null);
    await loadUnFormalizedZips();
  }, [editingId, updateExisting, createNew, loadUnFormalizedZips]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingItem(null);
    setEditingId(null);
  }, []);

  const handleFormalize = useCallback((item: UnFormalizedZipListItem) => {
    setFormalizingItem(item);
    setIsFormalizeModalOpen(true);
  }, []);

  const handleFormalizeClose = useCallback(() => {
    setIsFormalizeModalOpen(false);
    setFormalizingItem(null);
  }, []);

  const handleFormalizeSuccess = useCallback(() => {
    // Перезагружаем список после успешной формализации
    loadUnFormalizedZips(UnFormalizedZipResponseDto.status.NEW);
  }, [loadUnFormalizedZips]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Список ЗИПа</h1>
      </div>
      <UnFormalizedZipTable
        items={unFormalizedZips as unknown as UnFormalizedZipListItem[]}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onFormalize={handleFormalize}
      />

      <UnFormalizedZipForm
        isOpen={isModalOpen}
        editingItem={editingItem}
        isEditing={!!editingId}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />

      {formalizingItem && (
        <FormalizeModal
          isOpen={isFormalizeModalOpen}
          attachmentUuids={formalizingItem.docUrlUuids}
          unformalizedZipId={formalizingItem.id}
          engineId={formalizingItem.engineId as number}
          onClose={handleFormalizeClose}
          onSuccess={handleFormalizeSuccess}
        />
      )}
    </div>
  );
};
